import { PinEncryptionService, PinEntryFieldsFourDigits } from '@eika-kortbetjening/pin-code';

import { CardPinService } from '@/domain/card/CardPinService';

import { CardType, PinEncryptedMessage, SetPinSession, ViewPinSession } from '@generated-types';
import { cardTypeGet, cardTypePost, cardTypePut } from '@utils/query-utils';

import { getTypeCardBasePath, REST_BASE_URL } from '../common';

const setPinSession = async (cardType: CardType, cardId: string) =>
    cardTypeGet(cardType)<SetPinSession>(`${getTypeCardBasePath(cardType)}/${cardId}/pin/set-session`);

const setPinSessionFromAccountId = async (accountId: string) =>
    cardTypeGet(CardType.DEBIT)<SetPinSession>(`${REST_BASE_URL}/debit-accounts/${accountId}/pin/set-session`);

const putCardPin = async (cardType: CardType, cardId, audit: string, encryptedPinMessage: string, sgw: string) =>
    cardTypePut(cardType)<void>(`${getTypeCardBasePath(cardType)}/${cardId}/pin`, {
        audit,
        data: encryptedPinMessage,
        sgw,
    });

const putCardPinFromAccountId = async (accountId: string, audit: string, encryptedPinMessage: string, sgw: string) =>
    cardTypePut(CardType.DEBIT)<SetPinSession>(`${REST_BASE_URL}/debit-accounts/${accountId}/pin`, {
        audit,
        data: encryptedPinMessage,
        sgw,
    });

const getPinSession = async (cardType: CardType, cardId: string) =>
    cardTypeGet(cardType)<ViewPinSession>(`${getTypeCardBasePath(cardType)}/${cardId}/pin/view-session`);

export const postCardPin = async (
    audit: string,
    encryptedPinMessage: string,
    sgw: string,
    cardType: CardType,
    cardId: string,
) =>
    cardTypePost(cardType)<PinEncryptedMessage>(`${getTypeCardBasePath(cardType)}/${cardId}/pin`, {
        audit,
        data: encryptedPinMessage,
        undefined,
        sgw,
    });

export const setNewPin = async (cardType: CardType, cardId: string, newPin: PinEntryFieldsFourDigits) =>
    setPinSession(cardType, cardId)
        .then(({ audit, sgw, random, publicKey }) => {
            const pinString = newPin.join('');
            const pinObject = new PinEncryptionService(publicKey);
            const encryptedPinMessage = pinObject.generateEncryptedSetPinMessage(random, pinString);
            pinObject.encPin = pinString;
            return putCardPin(cardType, cardId, audit, encryptedPinMessage, sgw);
        })
        .catch((e) => {
            throw new Error(e);
        });

export const setNewPinFromAccountId = async (accountId: string, newPin: PinEntryFieldsFourDigits) =>
    setPinSessionFromAccountId(accountId)
        .then(async ({ audit, sgw, random, publicKey }) => {
            const pinString = newPin.join('');
            const pinObject = new PinEncryptionService(publicKey);
            const encryptedPinMessage = pinObject.generateEncryptedSetPinMessage(random, pinString);
            return putCardPinFromAccountId(accountId, audit, encryptedPinMessage, sgw);
        })
        .catch((e) => {
            throw new Error(e);
        });

export const getCardPin = async (cardType: CardType, cardId: string, cardNumber: string) => {
    const pinSession = await getPinSession(cardType, cardId);

    return CardPinService.encryptPin(pinSession, cardNumber, cardType, cardId);
};
